import React, { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export default function AdminEmailTemplates() {
  const [list, setList] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [current, setCurrent] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewVars, setPreviewVars] = useState({});
  const [testEmail, setTestEmail] = useState("");

  const load = async () => {
    const r = await api.get("/admin/email-templates");
    setList(r.data);
    if (!selectedKey && r.data.length) setSelectedKey(r.data[0].key);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  useEffect(() => {
    if (!selectedKey) return;
    api.get(`/admin/email-templates/${selectedKey}`).then((r) => {
      setCurrent(r.data);
      const v = {};
      (r.data.variables || []).forEach((x) => { v[x.name] = x.sample; });
      setPreviewVars(v);
      doPreview(r.data, v);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const doPreview = async (tpl, vars) => {
    try {
      const r = await api.post(`/admin/email-templates/${tpl.key}/preview`, { variables: vars });
      setPreview(r.data);
    } catch (e) {
      // ignore
    }
  };

  const save = async () => {
    if (!current) return;
    try {
      await api.put(`/admin/email-templates/${current.key}`, {
        subject: current.subject,
        html: current.html,
        enabled: current.enabled,
      });
      toast.success("Template saved");
      doPreview(current, previewVars);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const reset = async () => {
    if (!current) return;
    if (!window.confirm(`Reset '${current.key}' to default content?`)) return;
    try {
      const r = await api.post(`/admin/email-templates/${current.key}/reset`);
      setCurrent(r.data);
      doPreview(r.data, previewVars);
      toast.success("Reset to default");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a test email"); return; }
    try {
      await api.post(`/admin/email-templates/${current.key}/test-send`, {
        to: testEmail.trim(),
        variables: previewVars,
      });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMTP send failed");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-email-templates">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="bx-heading text-3xl font-extrabold">Email templates</h1>
            <p className="text-slate-400 mt-1">All transactional emails — edit subject + HTML with <code className="text-violet-400">{"{{variables}}"}</code>, live-preview, send test.</p>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* Left: template list */}
          <aside className="lg:col-span-3 bx-card p-3 space-y-1">
            {list.map((t) => (
              <button
                key={t.key}
                data-testid={`tpl-row-${t.key}`}
                onClick={() => setSelectedKey(t.key)}
                className={`w-full text-left px-3 py-2 rounded-md border ${selectedKey === t.key ? "bg-violet-500/10 border-violet-500/40" : "border-transparent hover:bg-white/5"}`}
              >
                <div className="font-medium text-sm text-white truncate">{t.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{t.key}</div>
              </button>
            ))}
          </aside>

          {/* Middle: editor */}
          <main className="lg:col-span-5 space-y-3">
            {current && (
              <div className="bx-card p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="bx-heading text-lg font-bold">{current.name}</div>
                  <label className="text-xs text-slate-300 inline-flex items-center gap-2">
                    <input type="checkbox" checked={current.enabled !== false} onChange={(e) => setCurrent({ ...current, enabled: e.target.checked })} />
                    Enabled
                  </label>
                </div>
                <div>
                  <label className="text-xs text-slate-400">Subject</label>
                  <input data-testid="tpl-subject" className="bx-input mt-1" value={current.subject || ""} onChange={(e) => setCurrent({ ...current, subject: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-slate-400">HTML body</label>
                  <textarea data-testid="tpl-html" className="bx-input bx-mono mt-1" rows={18} value={current.html || ""} onChange={(e) => setCurrent({ ...current, html: e.target.value })} />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button data-testid="tpl-save" onClick={save} className="bx-btn-primary text-sm"><Lucide.Save className="w-4 h-4 mr-1" /> Save</button>
                  <button data-testid="tpl-preview" onClick={() => doPreview(current, previewVars)} className="bx-btn-ghost text-sm"><Lucide.Eye className="w-4 h-4 mr-1" /> Preview</button>
                  <button data-testid="tpl-reset" onClick={reset} className="bx-btn-ghost text-sm"><Lucide.RotateCcw className="w-4 h-4 mr-1" /> Reset</button>
                </div>
              </div>
            )}
          </main>

          {/* Right: variables + preview + test send */}
          <aside className="lg:col-span-4 space-y-3">
            {current && (
              <>
                <div className="bx-card p-4">
                  <div className="bx-heading text-sm font-bold mb-2">Available variables</div>
                  <div className="space-y-2">
                    {(current.variables || []).map((v) => (
                      <div key={v.name} className="text-xs">
                        <div className="flex items-center justify-between">
                          <code className="text-violet-300">{`{{${v.name}}}`}</code>
                          <span className="text-slate-500">{v.description}</span>
                        </div>
                        <input
                          className="bx-input mt-1 text-xs"
                          value={previewVars[v.name] ?? ""}
                          onChange={(e) => {
                            const nv = { ...previewVars, [v.name]: e.target.value };
                            setPreviewVars(nv);
                            doPreview(current, nv);
                          }}
                          placeholder={v.sample || ""}
                        />
                      </div>
                    ))}
                    {(!current.variables || current.variables.length === 0) && (
                      <div className="text-xs text-slate-500">No variables defined for this template.</div>
                    )}
                  </div>
                </div>

                <div className="bx-card p-4">
                  <div className="bx-heading text-sm font-bold mb-2">Send test</div>
                  <input className="bx-input text-sm" placeholder="recipient@example.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} />
                  <button data-testid="tpl-test-send" onClick={sendTest} className="bx-btn-primary text-xs mt-2 w-full"><Lucide.Send className="w-3.5 h-3.5 mr-1" /> Send test</button>
                </div>

                {preview && (
                  <div className="bx-card p-0 overflow-hidden">
                    <div className="px-4 py-2 border-b border-white/5 text-xs">
                      <div className="text-slate-500">Subject preview</div>
                      <div className="text-white font-medium">{preview.subject}</div>
                    </div>
                    <iframe
                      title="Preview"
                      srcDoc={preview.html}
                      className="w-full"
                      style={{ height: 500, background: "#fff" }}
                    />
                  </div>
                )}
              </>
            )}
          </aside>
        </div>
      </div>
    </AdminLayout>
  );
}
